import { HideContainer } from '@components/hide-container';
import { RadioGroup } from '@components/radio-group';
import { SelectOption } from '@components/select';
import { InfoButton, Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingEditorValue } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';

import { createJourneyActionFilePath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { TimeSavedData } from 'api/journeys/requests';
import { JourneyActionDetailed, JourneyActionValuesTypes } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { TimeSavedContainer } from 'ui/helpers';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  DescriptionFormField,
  SidebarProps,
  createActionForm,
  useCreateActionFormStore,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import { FormWithUnsavedChangesPrompt } from '../../journey-builder/unsaved-changes-prompt';
import { TriggerMethod } from '../common';
import { getFieldMappingId } from '../helpers';

type ExtractTextData = {
  id: string;
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
  file?: FieldMappingEditorValue;
  url?: FieldMappingEditorValue;
  fileOrUrl: 'file' | 'url';
} & TimeSavedData;

const ACTION_TYPE = ACTION_TYPES.extract_text;
const Form = createActionForm<ExtractTextData>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction(journeyActionId);

  if (isLoading || !data) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  return (
    <Sidebar>
      <ActionHeader actionType={ACTION_TYPE} {...props}>
        <InfoButton>
          <Info />
        </InfoButton>
      </ActionHeader>
      <Sidebar.Content>
        <ExtractText journeyActionId={journeyActionId} action={data} />
      </Sidebar.Content>
    </Sidebar>
  );
};

const Info = () => {
  const { t } = useScopedTranslate(sidebarLocale);
  return (
    <Stack space={4}>
      <Text font="bold16" text={t('aboutExtract')} />
      <Text text={t('aboutExtractBase')} />
      <Text text={t('aboutExtractGuide')} />
    </Stack>
  );
};

const ExtractText = ({
  journeyActionId,
  action,
}: {
  journeyActionId: string;
  action: JourneyActionDetailed;
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateActionFormStore();
  const isRequired = formStore.useFieldValue('executionMethod') === 'automatic';
  const values = action.values as JourneyActionValuesTypes['extract_text'];
  const isFormDirty = formStore.useIsDirty();
  const isDirty = isFormDirty;
  const editMutation = useUpdateJourneyAction(journeyActionId);
  const fieldMappingId = getFieldMappingId(action.journey);
  const fileVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journey,
    types: ['file'],
  });
  const urlVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journey,
    types: ['text'],
  });
  const uploadMutation = useUploadFile(
    createJourneyActionFilePath(action.journey, action.shortId),
    true
  );

  const handleSave = (data: ExtractTextData) => {
    return editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      action: ACTION_TYPE,
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        fileOrUrl: data.fileOrUrl,
        file: data.file,
        url: data.url,
      },
      timeSaved: {
        days: data.days,
        hours: data.hours,
        minutes: data.minutes,
      },
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            formStore={formStore}
            hideLabel
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('extractText')}>
          <Stack space={4}>
            <Form.RadioGroup id="fileOrUrl" label={t('radio')} defaultValue={values?.fileOrUrl}>
              <RadioGroup.Option label={t('fileOption')} value="file" />
              <RadioGroup.Option label={t('urlOption')} helpText={t('urlOptionHelp')} value="url" />
            </Form.RadioGroup>
            <Form.ConditionalValue id="fileOrUrl" when={(value) => value === 'file'}>
              <Form.FieldMappingFileField
                id="file"
                label={t('file')}
                defaultValue={values?.file}
                variableGroups={fileVariableGroups}
                onSelect={uploadMutation.mutateAsync}
                validators={isRequired ? ['required'] : []}
                fieldMappingId={fieldMappingId}
              />
            </Form.ConditionalValue>
            <Form.ConditionalValue id="fileOrUrl" when={(value) => value === 'url'}>
              <Form.FieldMappingTextEditor
                id="url"
                label={t('url')}
                defaultValue={values?.url}
                singleLine
                plaintext
                variableGroups={urlVariableGroups}
                validators={isRequired ? ['required'] : []}
                fieldMappingId={fieldMappingId}
              />
            </Form.ConditionalValue>
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <TimeSavedContainer {...action.timeSaved} />
      </Stack>
      <ActionFooter action={action} isDisabled={!isDirty || editMutation.isLoading} form={Form} />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    radio: 'Extract text from',
    fileOption: 'File',
    urlOption: 'Online file',
    urlOptionHelp: '(URL)',
    extractText: 'Extract text',
    file: 'File',
    url: 'File URL',
    aboutExtract: 'About extracting text',
    aboutExtractBase:
      'Extract all text content from files in various formats. This action allows you to pull plain text from files, making it searchable, editable, and easier to process.',
    aboutExtractGuide:
      'Simply upload a document or select one created earlier in your journey to extract its text content.',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
  },
};
